import React, { useEffect, useRef, useState } from 'react'
import {
  Grid,
  Heading,
  Caption,
  ExpandableSection,
  Input,
  Form,
  Button,
} from '@enterprise-ui/canvas-ui-react'
import { twoDecimals } from '../Services/TwoDecimalPlaces'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import { FormikValues } from 'formik'
import { SET_FIELD } from './Constants'
import { useGlobalForm } from '../Context/GlobalFormStateContext'
import styles from './MultipleReasonCodes.module.css'
import './MultipleReasonCodes.css'

const formatCost = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    value,
  )

interface AmountCellRendererParams {
  value: any
  node: any
}

const AmountCellRenderer: React.FC<AmountCellRendererParams> = (params) => {
  const [state] = useGlobalForm()
  const val =
    params.value === undefined || params.value === null
      ? 0
      : parseFloat(params.value) || 0
  const [editing, setEditing] = useState(false)
  const [localVal, setLocalVal] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const startEdit = () => {
    if (state.isDocShowable || state.showRtvsDocuments) return
    setLocalVal(val === 0 ? '' : String(val))
    setEditing(true)
  }

  const commitAndMove = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      const parsed = parseFloat(localVal) || 0
      params.node.setDataValue('disputedAmount', parsed)
      setEditing(false)
      const nextIndex = e.shiftKey
        ? params.node.rowIndex - 1
        : params.node.rowIndex + 1
      const nextCell = document.querySelector(
        `[data-rtvs-amount-cell="${nextIndex}"]`,
      ) as HTMLElement
      if (nextCell) nextCell.focus()
    }
  }

  return (
    <div
      data-rtvs-amount-cell={params.node.rowIndex}
      className={editing ? styles.editableBoxFocused : styles.editableBox}
      tabIndex={
        state.isDocShowable || state.showRtvsDocuments || editing ? -1 : 0
      }
      onClick={startEdit}
      onFocus={startEdit}
    >
      {editing ? (
        <input
          ref={inputRef}
          type="number"
          min="0"
          step="0.01"
          tabIndex={-1}
          className={styles.editableInput}
          value={localVal}
          onKeyDown={commitAndMove}
          onBlur={(e) => {
            setEditing(false)
            const parsed = parseFloat(e.target.value) || 0
            params.node.setDataValue('disputedAmount', parsed)
          }}
          onChange={(e) => setLocalVal(e.target.value)}
        />
      ) : (
        <span>${val.toFixed(2)}</span>
      )}
    </div>
  )
}

interface MainProp {
  formik: FormikValues
  reasonCodes: Array<{ id: string; value: string; label: string }>
}

const MultipleReasonCodes: React.FC<MainProp> = ({ formik, reasonCodes }) => {
  const [state, dispatch] = useGlobalForm()
  const gridApiRef = useRef<any>(null)

  const [rtvsGridData, setRtvsGridData] = useState<any[]>([])
  const [totalDispute, setTotalDispute] = useState<number>(0)

  const totalDeduction = Number(formik.values?.originalDocumentAmount) || 0

  // Initialize grid rows from reason codes
  useEffect(() => {
    const initialData = reasonCodes.map((reason) => ({
      id: reason.id,
      disputeReason: reason.label,
      disputedAmount: 0,
    }))
    setRtvsGridData(initialData)
  }, [reasonCodes])

  // Recalculate total dispute whenever grid data changes
  useEffect(() => {
    if (rtvsGridData.length > 0) {
      const total = rtvsGridData.reduce(
        (sum: number, row: any) => sum + (parseFloat(row.disputedAmount) || 0),
        0,
      )
      setTotalDispute(total)

      // Sync non-zero rows to formik as multipleReasonCodes
      const multipleReasonCodes = rtvsGridData
        .filter((row) => parseFloat(row.disputedAmount) > 0)
        .map((row) => ({
          reasonCode: row.id,
          reasonCodeText: row.disputeReason,
          disputedDocumentAmount: parseFloat(row.disputedAmount),
        }))
      formik.setFieldValue('multipleReasonCodes', multipleReasonCodes)
      formik.setFieldValue('disputedDocumentAmount', total)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtvsGridData])

  const rtvsColumnDefs: ColDef[] = [
    {
      headerName: 'Dispute reasons',
      field: 'disputeReason',
      flex: 2.5,
      editable: false,
      wrapText: true,
      cellStyle: {
        paddingLeft: '16px',
        paddingTop: '8px',
        paddingBottom: '8px',
        lineHeight: '1.4',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      } as any,
    },
    {
      headerName: 'Disputed amount',
      field: 'disputedAmount',
      width: 240,
      editable: false,
      cellClass: 'rtvs-editable-cell',
      cellRenderer: AmountCellRenderer,
    },
  ]

  const onGridReady = (params: any) => {
    gridApiRef.current = params.api
  }

  const onCellValueChanged = (params: any) => {
    const updatedData = [...rtvsGridData]
    updatedData[params.rowIndex] = params.data
    setRtvsGridData(updatedData)
  }

  return (
    <div className="hc-pa-normal hc-pa-none hc-ma-none">
      <ExpandableSection startExpanded toggleLocation="left" padding="dense">
        <Heading size={6}>
          Enter the amount for each of the reasons you're disputing.
          <Caption below="You can dispute part or all of the deducted amount." />
        </Heading>
        <ExpandableSection.Content>
          <Grid.Container className="hc-pv-none hc-mv-none hc-ml-expanded">
            <Grid.Item xs={10} md={10} className="hc-mb-dense">
              <div
                style={{
                  width: '100%',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                }}
              >
                <div className="ag-theme-alpine">
                  <AgGridReact
                    columnDefs={rtvsColumnDefs}
                    rowData={rtvsGridData}
                    domLayout="autoHeight"
                    onGridReady={onGridReady}
                    onCellValueChanged={onCellValueChanged}
                    defaultColDef={{
                      sortable: false,
                      filter: false,
                      resizable: true,
                      suppressHeaderMenuButton: true,
                      suppressHeaderFilterButton: true,
                      suppressHeaderContextMenu: true,
                      wrapText: true,
                    }}
                    gridOptions={{
                      suppressMenuHide: true,
                      suppressContextMenu: true,
                      pagination: false,
                      rowHeight: 52,
                      suppressCellFocus: true,
                    }}
                  />
                </div>
              </div>
            </Grid.Item>

            {/* Totals summary pill */}
            <Grid.Item xs={10} md={10} className="hc-mt-dense hc-mb-dense">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #d0d0d0',
                  borderRadius: '16px',
                  padding: state.disputeSectionFlag ? '16px 20px' : '4px 20px',
                  backgroundColor: '#fff',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {state.disputeSectionFlag ? (
                    <>
                      <strong>Original Document Amount </strong>
                      <strong>{formatCost(totalDeduction)}</strong>
                    </>
                  ) : (
                    <div style={{ paddingTop: '8px' }}>
                      <Form.Field
                        id="originalDocumentAmount"
                        label="Original Document Amount $"
                        type="number"
                        style={{ width: '280px' }}
                        onChange={(e: any) => {
                          formik.setFieldValue(
                            'originalDocumentAmount',
                            twoDecimals(e.target.value),
                          )
                        }}
                        value={formik.values?.originalDocumentAmount ?? ''}
                        className="inputtype hc-ta-left"
                        error={
                          formik.errors.originalDocumentAmount !== undefined
                        }
                        errorText={formik.errors.originalDocumentAmount}
                        disabled={state.isDocShowable}
                        required
                      />
                    </div>
                  )}
                </div>
                <div>
                  <strong>Total dispute </strong>
                  <strong>{formatCost(totalDispute)}</strong>
                </div>
              </div>
              {totalDispute > totalDeduction && totalDeduction > 0 && (
                <Input.Info error className="hc-mt-dense">
                  Total dispute cannot be more than total deduction
                </Input.Info>
              )}
            </Grid.Item>
          </Grid.Container>
        </ExpandableSection.Content>
      </ExpandableSection>
      {!state.showRtvsDocuments && (
        <Grid.Container direction="row-reverse">
          <Grid.Item className="hc-mb-normal">
            <Button
              type="primary"
              disabled={
                totalDispute <= 0 ||
                totalDispute > totalDeduction ||
                (!state.disputeSectionFlag && totalDeduction <= 0)
              }
              onClick={() => {
                dispatch({
                  type: SET_FIELD,
                  payload: [{ id: 'showRtvsDocuments', value: true }],
                })
              }}
            >
              Next
            </Button>
          </Grid.Item>
        </Grid.Container>
      )}
    </div>
  )
}

export default MultipleReasonCodes
